import { getTokenFromLS } from "@dexteel/mesf-core";
import type {
	ComparisonConcept,
	CatalogOption,
	ComparisonResult,
	ReconciliationOutcome,
	ReconciliationPreview,
	ReferenceLinkChange,
	RollCutHistory,
	RollAssignmentHistory,
} from "./models";

export class ComparisonRequestError extends Error {
	constructor(
		message: string,
		public readonly requiresRerun: boolean,
	) {
		super(message);
	}
}

async function request<T>(
	path: string,
	signal: AbortSignal,
	body?: unknown,
): Promise<T> {
	const base =
		document.getElementsByTagName("base")[0]?.href ??
		window.location.origin + "/";
	const response = await fetch(
		new URL(`api/rollshop-comparison/${path}`, base),
		{
			method: body === undefined ? "GET" : "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${getTokenFromLS()}`,
			},
			body: body === undefined ? undefined : JSON.stringify(body),
			signal,
		},
	);
	if (!response.ok) {
		const error = await response.json().catch(() => null);
		throw new ComparisonRequestError(
			response.status === 403 || response.status === 499
				? "Administrator access is required. Sign in again if your session expired."
				: (error?.message ?? `Comparison request failed (${response.status}).`),
			error?.requiresRerun === true,
		);
	}
	return response.json();
}

export const getConcepts = (signal: AbortSignal) =>
	request<ComparisonConcept[]>("concepts", signal);
export const getRollCutHistory = (
	rollshopName: string | null,
	mesName: string | null,
	signal: AbortSignal,
) => {
	const query = new URLSearchParams();
	if (rollshopName) query.set("rollshopName", rollshopName);
	if (mesName) query.set("mesName", mesName);
	return request<RollCutHistory>(`roll-cuts?${query}`, signal);
};
export const getRollAssignmentHistory = (
	rollshopName: string | null,
	mesName: string | null,
	signal: AbortSignal,
) => {
	const query = new URLSearchParams();
	if (rollshopName) query.set("rollshopName", rollshopName);
	if (mesName) query.set("mesName", mesName);
	return request<RollAssignmentHistory>(`roll-assignments?${query}`, signal);
};
export const compareConcept = (
	concept: string,
	from: string,
	to: string,
	runId: string,
	signal: AbortSignal,
) =>
	request<ComparisonResult>("compare", signal, {
		concept,
		from,
		to,
		runId,
	});

export const getReferenceOptions = (concept: string, signal: AbortSignal) =>
	request<CatalogOption[]>(
		`reference-options/${encodeURIComponent(concept)}`,
		signal,
	);

export const saveReferenceLink = (
	snapshotToken: string,
	name: string,
	targetId: string | null,
	expectedTargetId: string | null,
	operation: "link" | "create" | "unlink",
	signal: AbortSignal,
) =>
	request<{ requiresRerun: boolean }>("reference-link", signal, {
		snapshotToken,
		name,
		targetId,
		expectedTargetId,
		operation,
	});

export const saveReferenceLinks = (
	snapshotToken: string,
	links: ReferenceLinkChange[],
	signal: AbortSignal,
) =>
	request<{ savedLinks: number }>("reference-links", signal, {
		snapshotToken,
		links,
	});

export const previewReconciliation = (
	snapshotToken: string,
	type: string,
	key: string | null,
	operation: "update" | "insert",
	signal: AbortSignal,
	page = 0,
	search = "",
	matchId?: string,
	includeReferencedNames = false,
) =>
	request<ReconciliationPreview>("preview", signal, {
		snapshotToken,
		type,
		key,
		operation,
		page,
		search,
		matchId,
		includeReferencedNames,
	});

export const applyReconciliation = (
	confirmationToken: string,
	signal: AbortSignal,
) => request<ReconciliationOutcome>("apply", signal, { confirmationToken });
